import React, { useEffect, useMemo, useState } from "react";
import { CircleActionButton } from "@src/components/CircleButton";
import ContentContainer from "@src/components/ContentContainer";
import PageTitle from "@src/components/PageTitle";
import PageTopSection from "@src/components/PageTopSection";
import {
  TopBarColumn,
  TopBarSection,
} from "@src/components/PageTopSection/PageTopSection";
import { SearchInput } from "@src/components/SearchInput";
import WrapperContainer from "@src/components/WrapperContainer";
import FilterIcon from "@src/assets/img/filter_icon.svg";
import FilterIconActive from "@src/assets/img/filter-icon-active.svg";
import { theme } from "@src/theme";
import { TotalLabel } from "@src/components/TotalLabel";
import ContentDrawer from "@src/components/ContentDrawer";
import TagFilter from "./TagFilter";
import UntaggedTracksTable from "./UntaggedTracksTable";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import { TrackPlayer } from "@src/components/MusicPlayer";
import { useFetchTrackAudioQuery } from "@src/queries/trackQueries";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { ITrack } from "@src/types/track";
import CreateTagModal from "./components/CreateTagModal";
import { RootState } from "@src/redux/store";
import {
  resetDeleteTagState,
  resetUpdateTagState,
  setCreateTagModalOpen,
  setDeleteTagState,
  setSelectedTrack,
  setUpdateTagState,
} from "@src/redux/slices/tagFactorySlice";
import DeleteTagModal from "./components/DeleteTagModal";
import UpdateTagModal from "./components/UpdateTagModal";

const TagFactory = () => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const {
    spaceUsedForTracks,
    totalTracksCount,
    selectedTrack,
    tagTypeToCreate,
    isCreateModalOpen,
    updateTagState,
    deleteTagState,
  } = useAppSelector((state: RootState) => state.tagFactory);
  const dispatch = useAppDispatch();
  const [selectedTrackUrl, setSelectedTrackUrl] = useState<string | null>(null);
  const { data: trackWithAudio } = useFetchTrackAudioQuery(
    selectedTrack?.documentId
  );
  const trackData = useMemo(
    () =>
      (trackWithAudio as AxiosResponse<ApiResponse<ITrack>>)?.data?.data ?? "",
    [trackWithAudio]
  );
  useEffect(() => {
    if (trackWithAudio && trackData) {
      setSelectedTrackUrl(trackData.audio.url as string);
    }
  }, [trackWithAudio]);

  const toggleCreateModal = ({ open }: { open: boolean }) => {
    dispatch(setCreateTagModalOpen(open));
  };

  useEffect(() => {
    return () => {
      setSelectedTrackUrl(null);
      dispatch(setCreateTagModalOpen(false));
      dispatch(setSelectedTrack(null));
    };
  }, []);

  return (
    <WrapperContainer>
      <PageTopSection>
        <TopBarSection columnCount={3}>
          <TopBarColumn>
            <PageTitle>Tag Factory</PageTitle>
            <CircleActionButton
              $tooltipContent="Filter"
              $active={isFilterDrawerOpen}
              onClick={() => {
                setIsFilterDrawerOpen(!isFilterDrawerOpen);
              }}
            >
              <img
                src={isFilterDrawerOpen ? FilterIconActive : FilterIcon}
                alt="filter"
                style={{ width: "70%", objectFit: "contain" }}
              />
            </CircleActionButton>
          </TopBarColumn>
          <TopBarColumn>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              {!!selectedTrack && (
                <TrackPlayer audioUrl={selectedTrackUrl as string} />
              )}
            </div>
          </TopBarColumn>
          <TopBarColumn>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-end",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                paddingBottom: theme.margins.xl,
              }}
            >
              <SearchInput placeholder="Search by name" />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  marginLeft: theme.margins.medium,
                }}
              >
                <TotalLabel grayText>
                  Space used: <TotalLabel>{spaceUsedForTracks}</TotalLabel>
                </TotalLabel>
                <TotalLabel grayText>
                  Total: <TotalLabel>{totalTracksCount}</TotalLabel>
                </TotalLabel>
              </div>
            </div>
          </TopBarColumn>
        </TopBarSection>
      </PageTopSection>
      <ContentContainer allowScroll>
        <ContentDrawer
          isOpen={isFilterDrawerOpen}
          toggleDrawer={() => {
            setIsFilterDrawerOpen(false);
          }}
        >
          <TagFilter
            onClose={() => {
              setIsFilterDrawerOpen(false);
            }}
          />
        </ContentDrawer>
        <CreateTagModal
          tagType={tagTypeToCreate}
          closeModal={() => {
            toggleCreateModal({ open: false });
          }}
          toggleModal={() => {
            toggleCreateModal({ open: !isCreateModalOpen });
          }}
          openModal={() => {
            toggleCreateModal({ open: true });
          }}
          isOpen={isCreateModalOpen}
          onConfirm={() => {}}
        />
        <UpdateTagModal
          tagType={updateTagState.tagType}
          closeModal={() => {
            dispatch(resetUpdateTagState());
          }}
          toggleModal={() => {
            dispatch(
              setUpdateTagState({ modalOpen: !updateTagState.modalOpen })
            );
          }}
          openModal={() => {
            dispatch(setUpdateTagState({ modalOpen: true }));
          }}
          isOpen={updateTagState.modalOpen}
          onConfirm={() => {}}
        />
        <DeleteTagModal
          tagType={deleteTagState.tagType}
          closeModal={() => {
            dispatch(resetDeleteTagState());
          }}
          toggleModal={() => {
            dispatch(
              setDeleteTagState({ modalOpen: !deleteTagState.modalOpen })
            );
          }}
          openModal={() => {
            dispatch(setDeleteTagState({ modalOpen: true }));
          }}
          isOpen={deleteTagState.modalOpen}
          onConfirm={() => {}}
        />
        <UntaggedTracksTable />
      </ContentContainer>
    </WrapperContainer>
  );
};

export default TagFactory;
